import React, { useState, useEffect } from 'react';
import { ArrowRight, Clock, Tag, Percent, TrendingUp, Star, Truck, Shield, Flame, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 4: PIPELINE
 * Style: Promotional, Deal-focused, Fast Fashion
 * Best for: Flash sales, Discount stores, Budget-friendly fashion
 * Inspired by: Shopify Pipeline theme
 */
const Template4 = () => {
    const navigate = useNavigate();

    // Countdown Timer for Flash Sale
    const [timeLeft, setTimeLeft] = useState({
        hours: 12,
        minutes: 45,
        seconds: 30
    });

    // Hero slider state
    const [currentSlide, setCurrentSlide] = useState(0);
    const slides = [
        { image: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1920&q=80', title: 'Summer Sale', subtitle: 'Up to 70% Off', badge: 'Limited Time' },
        { image: 'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=1920&q=80', title: 'New Arrivals', subtitle: 'Fresh Styles Weekly', badge: 'Just In' },
        { image: 'https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?w=1920&q=80', title: 'Clearance', subtitle: 'Final Markdowns', badge: 'Last Chance' }
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev.seconds > 0) {
                    return { ...prev, seconds: prev.seconds - 1 };
                } else if (prev.minutes > 0) {
                    return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
                } else if (prev.hours > 0) {
                    return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
                }
                return prev;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Auto-slide for hero
    useEffect(() => {
        const slideTimer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, 5000);
        return () => clearInterval(slideTimer);
    }, [slides.length]);

    return (
        <div className="min-h-screen bg-white">

            {/* ============================================
                PROMO BAR - Rotating deals
            ============================================ */}
            <div className="bg-rose-600 text-white py-2.5 px-4">
                <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
                    <Tag className="w-4 h-4" />
                    <span className="text-sm font-medium">FREE SHIPPING on orders over ₹999</span>
                    <span className="hidden sm:inline text-white/60">|</span>
                    <span className="hidden sm:inline text-sm">Use code: <span className="font-bold">SAVE20</span> for extra 20% off</span>
                </div>
            </div>

            {/* ============================================
                HERO SLIDER - Full width promotional
            ============================================ */}
            <section className="relative h-[50vh] sm:h-[60vh] lg:h-[70vh] overflow-hidden">
                {/* Slides */}
                {slides.map((slide, index) => (
                    <div
                        key={index}
                        className={`absolute inset-0 transition-opacity duration-700 ${
                            index === currentSlide ? 'opacity-100' : 'opacity-0'
                        }`}
                    >
                        <img
                            src={slide.image}
                            alt={slide.title}
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent"></div>

                        {/* Content */}
                        <div className="absolute inset-0 flex items-center">
                            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                                <div className="max-w-lg">
                                    <span className="inline-block bg-rose-600 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 mb-4">
                                        {slide.badge}
                                    </span>
                                    <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white mb-4 leading-tight">
                                        {slide.title}
                                    </h1>
                                    <p className="text-xl sm:text-2xl text-white/90 mb-6">
                                        {slide.subtitle}
                                    </p>
                                    <button
                                        onClick={() => navigate('/template4/shop')}
                                        className="group inline-flex items-center gap-3 bg-white text-gray-900 px-8 py-4 font-bold uppercase tracking-wide hover:bg-rose-600 hover:text-white transition-all duration-300"
                                    >
                                        Shop Now
                                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                {/* Slider Controls */}
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
                    {slides.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentSlide(index)}
                            className={`w-3 h-3 rounded-full transition-all ${
                                index === currentSlide ? 'bg-white w-8' : 'bg-white/50'
                            }`}
                        />
                    ))}
                </div>

                {/* Navigation Arrows */}
                <button
                    onClick={() => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                >
                    <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                    onClick={() => setCurrentSlide((prev) => (prev + 1) % slides.length)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                >
                    <ChevronRight className="w-6 h-6" />
                </button>
            </section>

            {/* ============================================
                FLASH SALE BANNER - Countdown timer
            ============================================ */}
            <section className="bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 py-6 sm:py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-4 text-white">
                            <div className="p-3 bg-white/20 rounded-full animate-pulse">
                                <Flame className="w-8 h-8" />
                            </div>
                            <div>
                                <h2 className="text-2xl sm:text-3xl font-black uppercase">Flash Sale</h2>
                                <p className="text-white/80">Up to 70% off on selected items</p>
                            </div>
                        </div>

                        {/* Countdown */}
                        <div className="flex items-center gap-3">
                            <Clock className="w-6 h-6 text-white" />
                            <span className="text-white font-medium">Ends in:</span>
                            <div className="flex items-center gap-2">
                                {[
                                    { value: timeLeft.hours, label: 'HRS' },
                                    { value: timeLeft.minutes, label: 'MIN' },
                                    { value: timeLeft.seconds, label: 'SEC' }
                                ].map((time, index) => (
                                    <React.Fragment key={time.label}>
                                        <div className="bg-white text-gray-900 px-3 py-2 rounded-lg text-center min-w-[60px]">
                                            <p className="text-2xl font-black">{String(time.value).padStart(2, '0')}</p>
                                            <p className="text-[10px] font-bold text-gray-500">{time.label}</p>
                                        </div>
                                        {index < 2 && <span className="text-white text-2xl font-bold">:</span>}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>

                        <button
                            onClick={() => navigate('/template4/shop')}
                            className="bg-white text-gray-900 px-8 py-3 font-bold uppercase tracking-wide hover:bg-gray-100 transition-colors"
                        >
                            Shop Sale
                        </button>
                    </div>
                </div>
            </section>

            {/* ============================================
                CATEGORIES - Quick shop cards
            ============================================ */}
            <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-2">Shop by Category</h2>
                        <p className="text-gray-500">Find your perfect style</p>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {[
                            { name: 'Women', discount: '50% OFF', image: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=400&h=500&fit=crop', color: 'from-pink-500' },
                            { name: 'Men', discount: '40% OFF', image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=500&fit=crop', color: 'from-blue-500' },
                            { name: 'Kids', discount: '60% OFF', image: 'https://images.unsplash.com/photo-1503919545889-aef636e10ad4?w=400&h=500&fit=crop', color: 'from-green-500' },
                            { name: 'Accessories', discount: '30% OFF', image: 'https://images.unsplash.com/photo-1523170335258-f5ed11844a49?w=400&h=500&fit=crop', color: 'from-amber-500' }
                        ].map((category, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template4/shop')}
                                className="group relative aspect-[3/4] rounded-2xl overflow-hidden cursor-pointer"
                            >
                                <img
                                    src={category.image}
                                    alt={category.name}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                />
                                <div className={`absolute inset-0 bg-gradient-to-t ${category.color} to-transparent opacity-60`}></div>
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center p-4">
                                    <span className="bg-rose-600 text-xs font-bold px-3 py-1 rounded-full mb-3">
                                        {category.discount}
                                    </span>
                                    <h3 className="text-2xl sm:text-3xl font-black uppercase mb-2">{category.name}</h3>
                                    <span className="text-sm font-medium opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                                        Shop Now →
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============================================
                TRENDING PRODUCTS - Hot deals section
            ============================================ */}
            <section className="py-12 sm:py-16 lg:py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
                        <div>
                            <div className="inline-flex items-center gap-2 text-rose-600 text-sm font-bold uppercase tracking-wider mb-2">
                                <TrendingUp className="w-5 h-5" />
                                Hot Right Now
                            </div>
                            <h2 className="text-3xl sm:text-4xl font-black text-gray-900">
                                Trending Products
                            </h2>
                        </div>
                        <button
                            onClick={() => navigate('/template4/shop')}
                            className="inline-flex items-center gap-2 text-gray-600 font-medium hover:text-rose-600 transition-colors"
                        >
                            View All <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {[
                            { name: 'Summer Dress', price: '₹1,499', originalPrice: '₹3,999', discount: '63%', rating: 4.8, reviews: 234, image: 'https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=400&h=500&fit=crop' },
                            { name: 'Denim Jacket', price: '₹2,199', originalPrice: '₹4,999', discount: '56%', rating: 4.9, reviews: 189, badge: 'BESTSELLER', image: 'https://images.unsplash.com/photo-1551028719-00167b16eac5?w=400&h=500&fit=crop' },
                            { name: 'Casual Sneakers', price: '₹1,799', originalPrice: '₹3,499', discount: '49%', rating: 4.7, reviews: 156, badge: 'NEW', image: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&h=500&fit=crop' },
                            { name: 'Crossbody Bag', price: '₹999', originalPrice: '₹2,499', discount: '60%', rating: 4.6, reviews: 98, image: 'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=400&h=500&fit=crop' },
                            { name: 'Floral Blouse', price: '₹899', originalPrice: '₹1,999', discount: '55%', rating: 4.5, reviews: 167, image: 'https://images.unsplash.com/photo-1564257631407-4deb1f99d992?w=400&h=500&fit=crop' },
                            { name: 'Slim Fit Jeans', price: '₹1,299', originalPrice: '₹2,999', discount: '57%', rating: 4.8, reviews: 245, badge: 'TRENDING', image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?w=400&h=500&fit=crop' },
                            { name: 'Watch Classic', price: '₹2,499', originalPrice: '₹5,999', discount: '58%', rating: 4.9, reviews: 312, image: 'https://images.unsplash.com/photo-1524592094714-0f0654e20314?w=400&h=500&fit=crop' },
                            { name: 'Sunglasses', price: '₹699', originalPrice: '₹1,499', discount: '53%', rating: 4.4, reviews: 87, image: 'https://images.unsplash.com/photo-1572635196237-14b3f281503f?w=400&h=500&fit=crop' }
                        ].map((product, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template4/shop')}
                                className="group bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl hover:border-rose-200 transition-all duration-300 cursor-pointer"
                            >
                                {/* Product Image */}
                                <div className="relative aspect-[4/5] overflow-hidden bg-gray-50">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />

                                    {/* Discount Badge */}
                                    <div className="absolute top-3 left-3 bg-rose-600 text-white text-xs font-bold px-2 py-1 rounded">
                                        -{product.discount}
                                    </div>

                                    {/* Special Badge */}
                                    {product.badge && (
                                        <div className="absolute top-3 right-3 bg-gray-900 text-white text-xs font-bold px-2 py-1 rounded">
                                            {product.badge}
                                        </div>
                                    )}

                                    {/* Quick Add */}
                                    <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                                        <button className="w-full bg-gray-900 text-white py-2.5 text-sm font-bold uppercase tracking-wide hover:bg-rose-600 transition-colors">
                                            Add to Cart
                                        </button>
                                    </div>
                                </div>

                                {/* Product Info */}
                                <div className="p-4">
                                    <div className="flex items-center gap-1 mb-2">
                                        <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                                        <span className="text-sm font-medium text-gray-700">{product.rating}</span>
                                        <span className="text-xs text-gray-400">({product.reviews})</span>
                                    </div>
                                    <h3 className="text-sm font-medium text-gray-900 mb-2 line-clamp-1 group-hover:text-rose-600 transition-colors">
                                        {product.name}
                                    </h3>
                                    <div className="flex items-center gap-2">
                                        <span className="text-lg font-bold text-rose-600">{product.price}</span>
                                        <span className="text-sm text-gray-400 line-through">{product.originalPrice}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============================================
                PROMO BANNER - Dual promotion
            ============================================ */}
            <section className="py-8 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {[
                            { title: 'New Season Arrivals', subtitle: 'Check out all the new trends', cta: 'Shop Collection', bg: 'bg-gradient-to-r from-purple-600 to-indigo-600', image: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=800&h=400&fit=crop' },
                            { title: 'Clearance Zone', subtitle: 'Last chance to grab these deals', cta: 'Shop Clearance', bg: 'bg-gradient-to-r from-rose-600 to-pink-600', image: 'https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?w=800&h=400&fit=crop' }
                        ].map((promo, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template4/shop')}
                                className="relative h-64 rounded-2xl overflow-hidden cursor-pointer group"
                            >
                                <img
                                    src={promo.image}
                                    alt={promo.title}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className={`absolute inset-0 ${promo.bg} opacity-70`}></div>
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center p-6">
                                    <h3 className="text-2xl sm:text-3xl font-black uppercase mb-2">{promo.title}</h3>
                                    <p className="text-white/80 mb-4">{promo.subtitle}</p>
                                    <span className="inline-flex items-center gap-2 bg-white text-gray-900 px-6 py-2 font-bold text-sm uppercase tracking-wide group-hover:bg-gray-100 transition-colors">
                                        {promo.cta} <ArrowRight className="w-4 h-4" />
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============================================
                VIDEO SHOWCASE - Fashion videos
            ============================================ */}
            <section className="py-12 sm:py-16 lg:py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-2">Style Inspiration</h2>
                        <p className="text-gray-500">Watch our latest lookbooks and style guides</p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {[
                            { title: 'Summer Lookbook 2024', views: '12.5K', thumbnail: 'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=600&h=400&fit=crop' },
                            { title: 'Styling Tips & Tricks', views: '8.2K', thumbnail: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=600&h=400&fit=crop' },
                            { title: 'Behind The Scenes', views: '5.7K', thumbnail: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop' }
                        ].map((video, index) => (
                            <div key={index} className="group cursor-pointer">
                                <div className="relative aspect-video rounded-xl overflow-hidden mb-4">
                                    <img
                                        src={video.thumbnail}
                                        alt={video.title}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors">
                                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                                            <Play className="w-6 h-6 text-gray-900 ml-1" fill="currentColor" />
                                        </div>
                                    </div>
                                </div>
                                <h3 className="font-bold text-gray-900 mb-1 group-hover:text-rose-600 transition-colors">{video.title}</h3>
                                <p className="text-sm text-gray-500">{video.views} views</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============================================
                TRUST BADGES - Why shop with us
            ============================================ */}
            <section className="py-10 bg-gray-900 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            { icon: Truck, title: 'Free Shipping', desc: 'On orders over ₹999' },
                            { icon: Shield, title: 'Secure Payment', desc: '100% protected' },
                            { icon: Percent, title: 'Best Prices', desc: 'Price match guarantee' },
                            { icon: Star, title: '5-Star Reviews', desc: '10,000+ happy customers' }
                        ].map((feature, index) => (
                            <div key={index} className="text-center">
                                <div className="inline-flex items-center justify-center w-14 h-14 bg-rose-600 rounded-full mb-4">
                                    <feature.icon className="w-7 h-7" />
                                </div>
                                <h3 className="font-bold mb-1">{feature.title}</h3>
                                <p className="text-gray-400 text-sm">{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============================================
                NEWSLETTER - Get exclusive deals
            ============================================ */}
            <section className="py-16 sm:py-20 bg-rose-600">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6">
                        <Tag className="w-4 h-4 text-white" />
                        <span className="text-white text-sm font-medium">Exclusive Offers</span>
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
                        Get 20% Off Your First Order
                    </h2>
                    <p className="text-white/90 mb-8">
                        Subscribe to our newsletter and never miss a deal. Plus, get exclusive access to flash sales!
                    </p>
                    <form className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-6 py-4 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white"
                        />
                        <button
                            type="submit"
                            className="px-8 py-4 bg-gray-900 text-white font-bold uppercase tracking-wide rounded-lg hover:bg-gray-800 transition-colors"
                        >
                            Subscribe
                        </button>
                    </form>
                    <p className="text-white/70 text-sm mt-4">
                        No spam ever. Unsubscribe anytime.
                    </p>
                </div>
            </section>

            {/* ============================================
                INSTAGRAM FEED - Social proof
            ============================================ */}
            <section className="py-12 bg-white">
                <div className="text-center mb-8">
                    <p className="text-gray-500 text-sm mb-1">Follow us on Instagram</p>
                    <p className="text-gray-900 font-bold text-lg">@pipelinefashion</p>
                </div>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-1">
                    {[
                        'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=300&h=300&fit=crop',
                        'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=300&h=300&fit=crop',
                        'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=300&h=300&fit=crop',
                        'https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?w=300&h=300&fit=crop',
                        'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=300&h=300&fit=crop',
                        'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=300&h=300&fit=crop'
                    ].map((image, index) => (
                        <div key={index} className="relative aspect-square overflow-hidden group cursor-pointer">
                            <img
                                src={image}
                                alt={`Instagram ${index + 1}`}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-rose-600/0 group-hover:bg-rose-600/40 transition-colors flex items-center justify-center">
                                <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                </svg>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

        </div>
    );
};

export default Template4;
